import { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import "./NcdForm.css";
import { validateForm } from "../../../utils/validationUtils";
import { ncdAPI } from "../../../services/ncd.api";
import { useNCD } from "../hooks/useNCD";
import { CommonContext } from "../../../context/CommonContext";

export const Personalexamination = ({ individualId, viewMode, selectedUser, setUserError }) => {

    const { screeningData, setLoading, setScreeningData, setError, clearScreeningData } = useNCD();

    const { updateFieldValues } = useContext(CommonContext);

    const today = new Date().toISOString().split("T")[0];
    const [formErrors, setFormErrors] = useState({});

    const perexamValue = screeningData[`personalExamination_${individualId}`];

    const [perexamform, setPerExamform] = useState({
        height: "",
        bmi: "",
        weight: "",
        personalExamDate: "",
        examination: null,
        referral: null
    });

    useEffect(() => {

        const fetchPersonalExamination = async () => {
            try {
                if (!individualId) return;

                setLoading(true);
                const fetchedValue = await ncdAPI.getPersonalExamination(individualId);

                setScreeningData(`personalExamination_${individualId}`, fetchedValue.data || {});
                setLoading(false);
            } catch (error) {
                setError("Error fetching personal examination data");
                setLoading(false);
            }
        };

        if (viewMode === 'VIEW' && !perexamValue) {
            fetchPersonalExamination();
        } else {
            setLoading(false);
        }

    }, [individualId, viewMode]);

    const handleperexam = (e) => {
        const { name, value, type } = e.target;

        if (type === "date") {
            const selectedDate = new Date(value);

            const formattedDate = selectedDate
                .toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                })
                .replace(/\//g, "-"); // Format to dd-mm-yyyy

            setPerExamform((prevForm) => ({
                ...prevForm,
                [name]: formattedDate,
            }));
        } else {
            setPerExamform((prevForm) => {
                const updatedForm = {
                    ...prevForm,
                    [name]: value,
                };

                if (name === "height" || name === "weight") {
                    const height = name === "height" ? parseFloat(value) : parseFloat(updatedForm.height);
                    const weight = name === "weight" ? parseFloat(value) : parseFloat(updatedForm.weight);

                    if (height > 0 && weight > 0) {
                        const bmi = (weight / (height / 100) ** 2).toFixed(2);

                        updatedForm.bmi = bmi; // Set the calculated BMI
                    } else {
                        updatedForm.bmi = ""; // Reset BMI if height or weight is blank or invalid
                    }
                }

                // Clear BMI error if BMI is calculated and not empty
                if (updatedForm.bmi && !isNaN(updatedForm.bmi)) {
                    setFormErrors((prevErrors) => ({
                        ...prevErrors,
                        bmi: "",
                    }));
                }

                return updatedForm;
            });
        }

        setFormErrors({ ...formErrors, [e.target.name]: "" });
    };

    const perExamSchema = {
        personalExamDate: { required: true, message: "This field is required." },
        height: {
            required: true,
            type: "number",
            min: 50,
            max: 240,
            message: "Enter valid value (50-240 cm).",
        },
        weight: {
            required: true,
            type: "number",
            min: 2,
            max: 240,
            message: "Enter valid value (2-240 Kg).",
        },
        bmi: {
            required: true,
            type: "number",
            message: "This field is required.",
        }
    };

    const perExamSubmit = async (e) => {
        e.preventDefault();

        const errors = validateForm(perexamform, perExamSchema);
        // If there are errors, update state and do not submit
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        if (!selectedUser) {
            setUserError(true);
            toast.error("Please select SHC staff", { className: "custom-toast-error" });
            return false;
        }

        try {
            setLoading(true);

            const payload = {
                ...perexamform,
                createdUser: selectedUser,
                screeningANM: {
                    users: selectedUser,
                    anmValue: selectedUser,
                    viaTrained: true
                }
            };

            const response = await ncdAPI.submitPersonalExam(payload, individualId);

            if (response.success) {

                // Update context with new values from API response

               // updateFieldValues(response.data, { NCD: "programm" });

                toast.success(`Personal exam saved successfully`);

                // Clear Old data after successful save

                clearScreeningData(`personalExamination_${individualId}`);

                // Reset form
                setPerExamform({
                    height: "",
                    bmi: "",
                    weight: "",
                    personalExamDate: "",
                    createdUser: selectedUser || "",
                    screeningANM: {
                        users: selectedUser,
                        anmValue: selectedUser,
                        viaTrained: null
                    },
                    examination: null,
                    referral: null
                });
            } else {
                toast.error(response.message || "Failed to save data", { className: "custom-toast-error" });
            }
            setLoading(false);
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong", { className: "custom-toast-error" });
            setLoading(false);
        }
    };

    return (
        <>

            <div>
                <form onSubmit={perExamSubmit}>
                    <div className="personal-exam-row">
                        <div className="exam-item">
                            <span className="exam-label">Date {viewMode === "ADD" && <span className="text-danger">*</span>}</span>
                            {viewMode === "ADD" ? (
                                <>
                                    <input type="date" className="exam-textarea" name="personalExamDate" onChange={handleperexam} max={today}></input>
                                    {formErrors.personalExamDate && <p className="error">{formErrors.personalExamDate}</p>}
                                </>
                            ) : (
                                <span className="exam-value">{perexamValue?.personalExamDate ?? "--"}</span>
                            )}
                        </div>
                        <div className="exam-item">
                            <span className="exam-label">Height (in cm) {viewMode === "ADD" && <span className="text-danger">*</span>}</span>
                            {viewMode === "ADD" ? (
                                <>
                                    <input type="text" className="exam-textarea" name="height" maxLength={3} onChange={handleperexam}></input>
                                    {formErrors.height && <p className="error">{formErrors.height}</p>}
                                </>
                            ) : (
                                <span className="exam-value">{perexamValue?.height ?? "--"}</span>
                            )}
                        </div>
                        <div className="exam-item">
                            <span className="exam-label">Weight (in kg) {viewMode === "ADD" && <span className="text-danger">*</span>}</span>
                            {viewMode === "ADD" ? (
                                <>
                                    <input type="text" className="exam-textarea" name="weight" maxLength={3} onChange={handleperexam} ></input>
                                    {formErrors.weight && <p className="error">{formErrors.weight}</p>}
                                </>
                            ) : (
                                <span className="exam-value">{perexamValue?.weight ?? "--"}</span>
                            )}
                        </div>
                        <div className="exam-item">
                            <span className="exam-label">BMI {viewMode === "ADD" && <span className="text-da"></span>}</span>
                            {viewMode === "ADD" ? (
                                <>
                                    <input type="text" className="exam-textarea" name="bmi" value={perexamform.bmi} onChange={handleperexam} readOnly></input>
                                    {formErrors.bmi && <p className="error">{formErrors.bmi}</p>}
                                </>
                            ) : (
                                <span className="exam-value">{perexamValue?.bmi ?? "--"}</span>
                            )}
                        </div>
                    </div>
                    {viewMode === "ADD" && (
                        <div className="save-button-container">
                            <button type="submit" className="save-button">Save</button>
                        </div>
                    )}

                    <div className="separator"></div>
                </form>
            </div>
        </>
    );
};
